import {
  FormGroup,
  FormGroupFeedback,
  IFormGroupProps,
  Intent,
  INumericInputProps,
  NumericInput,
} from '@pinpointhq/thumbtack';
import { useField } from 'formik';
import * as React from 'react';

export interface INumberFieldProps {
  children?: React.ReactNode;
  name: string;
  formGroupProps?: Omit<IFormGroupProps, 'labelFor'>;
  fill?: boolean;
  inputProps?: Omit<INumericInputProps, 'type' | 'intent'>;
  placeholder?: string;
}

export function NumberField<T extends string>({
  children,
  name,
  fill = true,
  formGroupProps,
  inputProps,
  placeholder,
}: INumberFieldProps) {
  const [field, meta, helpers] = useField<T>(name);
  const hasError = meta.touched && meta.error;

  /**
   * NOTE: (TomLuce) Appreciate that we're treating a number as a string, but if we dont then we can't type
   * numbers with things like periods, because if we're trying to type 11.25 and we type '11.' then it won't
   * set it as the formik value as it's not a valid number. Fortunately the NumericInput has its value typed
   * to number | string. I believe we're only having this issue as we're in controlled mode.
   */
  const handleChange = (valueAsNumber: number, valueAsString: string, inputElement: HTMLInputElement | null) => {
    helpers.setValue(valueAsString ? (valueAsString as T) : null);
  };

  return (
    <FormGroup {...formGroupProps} labelFor={name}>
      {children}
      <NumericInput
        fill={fill}
        {...field}
        {...inputProps}
        intent={hasError ? Intent.DANGER : Intent.NONE}
        onValueChange={handleChange}
        placeholder={placeholder}
      />
      {hasError && <FormGroupFeedback intent={Intent.DANGER}>{meta.error}</FormGroupFeedback>}
    </FormGroup>
  );
}

export default NumberField;
